import { useState, useEffect } from "react";
import { Product } from "../../__generated__/graphql";
import ResponsiveCDNImage from "../common/image/responsive-cdn-image";
import ReactLoadingSkeleton from "react-loading-skeleton";
import { FaHeart } from "react-icons/fa";
import { HiBolt } from "react-icons/hi2";
import NewTag from "./new-tag";
import { StoreCoupon } from "./types";
import { CouponBadge } from "./coupon-badge";

interface IProp {
  onClick: () => void;
  title: string;
  closingHours: string;
  discountPrice?: number | null | undefined;
  price?: number | null | undefined;
  img: string;
  distance?: number | null;
  products: Product[];
  likeCount?: number;
  signupDate?: Date;
  availableStoreCoupon?: StoreCoupon;
}

export const StoreCardForTimesail = ({
  title,
  closingHours,
  discountPrice,
  price,
  onClick,
  img,
  distance,
  products,
  likeCount,
  signupDate,
  availableStoreCoupon,
}: IProp): JSX.Element => {
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const closingTime = new Date();
      const [hours, minutes] = closingHours.split(":").map(Number);
      closingTime.setHours(hours, minutes, 0, 0);

      const now = new Date();
      const difference = closingTime.getTime() - now.getTime();

      if (difference > 0) {
        const hoursLeft = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutesLeft = Math.floor((difference / (1000 * 60)) % 60);
        setTimeLeft(`${hoursLeft}시간 ${minutesLeft}분 남음`);
      } else {
        setTimeLeft("마감되었습니다");
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000); // 1분마다 업데이트

    return () => clearInterval(timer);
  }, [closingHours]);

  return (
    <>
      <div className="w-full" onClick={onClick}>
        <div className="w-full h-full rounded-[12px] overflow-visible shadow-lg relative">
          <div className="mb-2 flex flex-row gap-1 scrollbar-hide overflow-x-scroll w-full relative">
            <div className="w-[180px] h-[160px] flex-shrink-0 relative">
              <div className="absolute bottom-0 left-0 w-full h-full rounded-2xl bg-gradient-to-t from-black to-transparent opacity-30"></div>
              {isImageLoading && (
                <div className={`w-full h-full absolute left-0`}>
                  {/* align-top으로 단차를 없앱니다 */}
                  <ReactLoadingSkeleton className="w-full h-full align-top" />
                </div>
              )}
              <ResponsiveCDNImage
                alt="store-img"
                src={img}
                className="rounded-xl w-full h-full object-cover"
                onLoad={() => setIsImageLoading(false)}
              />
              <div className="absolute bottom-2 left-4 right-4 font-bold text-white z-10 text-xl flex items-center min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{title}</span>
                  <span className="flex-shrink-0">
                    <NewTag signupDate={signupDate} />
                  </span>
                </div>
              </div>
              <div className="absolute top-2 left-2 flex gap-1 z-10">
                <div className="bg-gradient-to-r from-[#ff4757] to-[#ff6b35] font-bold text-white p-1 px-3 rounded-full shadow-lg flex items-center gap-1">
                  <HiBolt className="w-3 h-3" />
                  <p className="text-sm">바로픽업</p>
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-2">
              {products.map((product, productIndex) => (
                <div
                  key={product.menus?.[0]?.menu?.id}
                  className="flex flex-row gap-0.5 relative rounded-2xl overflow-hidden"
                >
                  {product.isEnd && product.currentCount !== 0 && (
                    <div className="absolute inset-0 z-10 bg-black bg-opacity-50 flex items-center justify-center rounded-xl">
                      <span className="text-white text-xl font-semibold">
                        판매종료
                      </span>
                    </div>
                  )}
                  {product.currentCount <= 0 && (
                    <div className="absolute inset-0 z-20 bg-black bg-opacity-50 flex items-center justify-center rounded-xl">
                      <span className="text-white text-xl font-semibold">
                        품절
                      </span>
                    </div>
                  )}
                  {/* 가격 라벨 - PriceLabel 스타일 복사 */}
                  {product.currentCount > 0 && (
                    <div className="z-10 absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/90 to-transparent">
                      <div className="text-white text-sm font-semibold">
                        {product.userPrice?.toLocaleString()}원
                      </div>
                    </div>
                  )}

                  {/* 제품 제목 - 하단 그라데이션 배경 */}
                  <div className="z-10 absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                    <div className="text-white text-sm font-semibold line-clamp-2">
                      {product?.name}
                    </div>
                  </div>
                  {product?.img && (
                    <div className="flex gap-0.5 overflow-hidden rounded-2xl">
                      <div className="w-[140px] h-[160px] flex-shrink-0 relative">
                        <ResponsiveCDNImage
                          alt="product-img"
                          src={product.img as string}
                          className={`w-full h-full object-cover rounded-xl`}
                          sizeRatio={1 / 3}
                        />
                      </div>
                    </div>
                  )}
                  {product?.menus && product?.menus?.length > 0 && (
                    <div className="flex gap-0.5 overflow-hidden rounded-2xl">
                      {[...product?.menus].reverse().map((menu, menuIndex) => (
                        <div
                          key={menu.menu?.id}
                          className="w-[140px] h-[160px] flex-shrink-0 relative"
                        >
                          <ResponsiveCDNImage
                            alt="menu-img"
                            src={menu.menu?.img as string}
                            className={`w-full h-full object-cover ${
                              productIndex === products.length - 1 &&
                              menuIndex ===
                                (product.menus?.length as number) - 1
                                ? "rounded-r-xl"
                                : ""
                            }`}
                            sizeRatio={1 / 3}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 쿠폰 칩 - 제품 리스트와 Lower Box 사이에 오버레이 */}
          {availableStoreCoupon && (
            <div
              className="absolute left-1/2 transform -translate-x-1/2 z-50 flex justify-center"
              style={{ top: "calc(160px - 1rem)" }}
            >
              <CouponBadge coupon={availableStoreCoupon} />
            </div>
          )}

          {/* Lower Box */}
          <div className="w-full px-4 pt-2 pb-3 flex justify-between">
            {/* Left Box */}
            <div className="flex flex-col items-start justify-center">
              <div className="flex gap-1 items-end font-semibold text-black text-lg mb-1 flex-wrap min-w-0">
                <span className="truncate max-w-[200px]">{title} 타임세일</span>
                {likeCount && likeCount > 0 ? (
                  <span className="text-gray-500 text-sm break-keep flex items-center gap-1 pb-0.5">
                    (<FaHeart className="w-4 h-4 text-[#1562fc]" />{" "}
                    <p className="">{likeCount}</p>)
                  </span>
                ) : null}
              </div>
              <div className="flex">
                <p className="text-base text-gray-600 mb-0.5">
                  <span className="font-medium text-base">
                    {`${closingHours}까지 픽업가능`}
                  </span>
                </p>
              </div>
              <span className="text-base text-gray-500">
                {distance
                  ? distance < 1
                    ? (distance * 1000).toFixed(0) + "m"
                    : distance.toFixed(2) + "km"
                  : ""}
              </span>
              <span className="text-base text-red-500 h-5">{timeLeft}</span>
            </div>

            {/* Right Box */}
            <div className="flex flex-col items-end justify-center">
              <p className="font-bold text-gray-400 text-md line-through">
                {price ? `${price?.toLocaleString()}원` : null}
              </p>
              <p className="font-bold text-[#1562fc] text-2xl ml-4 whitespace-nowrap">
                {discountPrice ? `${discountPrice?.toLocaleString()}원` : null}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
